// Premium loader controller - value-driven steps with custom animation
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "animationContainer", "progressCircle", "searchIcon", "successIcon",
    "textContainer", "actionText", "locationText",
    "step", "stepNumber", "stepSpinner", "stepCheck", "stepTitle", "stepDesc"
  ]

  static values = {
    location: { type: String, default: "Ihrer Nähe" },
    foundText: { type: String, default: "Anbieter gefunden" },
    providerCount: { type: Number, default: 3 }
  }

  connect() {
    this.timeouts = []
    this.circumference = 2 * Math.PI * 46 // matches r=46 in SVG
    this.startAnimation()
  }

  disconnect() {
    this.timeouts.forEach(t => clearTimeout(t))
  }

  startAnimation() {
    const steps = this.stepTargets
    const stepDuration = 1533 // Time per step — 3 × 1533 = ~4.6s, +1s vs previous 1200ms

    steps.forEach((step, index) => {
      // Activate step (show spinner)
      this.addTimeout(() => {
        this.activateStep(index)
        this.animateProgress((index + 0.5) / steps.length)
      }, index * stepDuration)

      // Complete step (show check)
      this.addTimeout(() => {
        this.completeStep(index)
        this.animateProgress((index + 1) / steps.length)
      }, index * stepDuration + stepDuration - 250)
    })

    // Total time for steps
    const stepsTime = steps.length * stepDuration

    // Reveal the success state (search icon → checkmark, location greened) once
    // the tiles and progress ring have finished.
    this.addTimeout(() => {
      this.showSuccess()
    }, stepsTime + 1130)

    // Tell the parent lead-funnel controller the cosmetic animation has finished
    // so it can advance the funnel. Advancing is event-driven (not a fixed dwell),
    // so every tile, the progress ring and the success checkmark always play out
    // regardless of how many tiles a brand's loader renders. SUCCESS_LINGER holds
    // the checkmark on screen briefly before we move on.
    const SUCCESS_LINGER = 500
    this.addTimeout(() => {
      this.dispatch("complete")
    }, stepsTime + 1130 + SUCCESS_LINGER)
  }

  activateStep(index) {
    const step = this.stepTargets[index]
    if (!step) return

    // Mark as active
    step.classList.add('active')
    step.classList.remove('completed')

    // Show spinner, hide number
    const numberEl = this.stepNumberTargets[index]
    const spinnerEl = this.stepSpinnerTargets[index]
    
    if (numberEl) numberEl.classList.add('hidden')
    if (spinnerEl) spinnerEl.classList.remove('hidden')
  }

  completeStep(index) {
    const step = this.stepTargets[index]
    if (!step) return

    // Mark as completed
    step.classList.remove('active')
    step.classList.add('completed')

    // Hide spinner, show check
    const spinnerEl = this.stepSpinnerTargets[index]
    const checkEl = this.stepCheckTargets[index]
    
    if (spinnerEl) spinnerEl.classList.add('hidden')
    if (checkEl) {
      checkEl.classList.remove('hidden')
      checkEl.classList.add('flex')
    }
  }

  animateProgress(fraction) {
    if (!this.hasProgressCircleTarget) return
    const offset = this.circumference * (1 - fraction)
    this.progressCircleTarget.style.strokeDashoffset = offset
  }

  showSuccess() {
    // Swap the search icon for the success checkmark — the only completion beat.
    // We intentionally do NOT recolour/collapse the header: doing so turned the
    // location green and made it jump upward as the action line collapsed, which
    // read as a janky shift right before advancing.
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.add('hidden')
    }
    if (this.hasSuccessIconTarget) {
      this.successIconTarget.classList.remove('hidden')
    }
  }

  addTimeout(callback, delay) {
    const timeout = setTimeout(callback, delay)
    this.timeouts.push(timeout)
    return timeout
  }
}
